<template>
    <div class="fc-page">
        <div class="fc-wrapper">

            <!-- Error state -->
            <div v-if="initError" class="mp-consent-card">
                <div class="mp-consent-error-icon">
                    <i class="bi bi-exclamation-triangle-fill"></i>
                </div>
                <h3 class="mp-consent-heading">{{ $t('marketplace.forgeConsent.invalidRequest') }}</h3>
                <p class="mp-consent-sub">{{ initError }}</p>
                <button class="mp-consent-btn mp-consent-btn--secondary mt-4" @click="deny">
                    {{ $t('cancel') }}
                </button>
            </div>

            <!-- Consent card -->
            <div v-else class="mp-consent-card">

                <!-- App identity -->
                <div class="mp-consent-product mb-0">
                    <div class="mp-consent-product-icon">
                        <i class="bi bi-grid-3x3-gap-fill"></i>
                    </div>
                    <div class="mp-consent-product-info">
                        <h2 class="mp-consent-product-name">{{ appDisplayName }}</h2>
                        <span class="fc-app-badge">
                            <i class="bi bi-cpu mr-1"></i>
                            {{ $t('marketplace.forgeConsent.appBadge') }}
                        </span>
                    </div>
                </div>

                <hr class="mp-consent-divider" />

                <!-- Request description -->
                <div class="fc-request-block">
                    <div class="fc-request-title">
                        <i class="bi bi-shield-lock-fill mr-2"></i>
                        {{ $t('marketplace.forgeConsent.requestsPermissions') }}
                    </div>
                    <p v-if="reason" class="fc-request-reason">{{ reason }}</p>
                </div>

                <!-- Permission list -->
                <div class="fc-perm-list">
                    <div
                        v-for="(perm, idx) in permissions"
                        :key="idx"
                        class="fc-perm-row"
                    >
                        <i class="bi bi-lightning-fill fc-perm-icon"></i>
                        <code class="fc-perm-name">{{ perm }}</code>
                    </div>
                </div>

                <!-- Success state -->
                <div v-if="done" class="mp-consent-success">
                    <i class="bi bi-check-circle-fill"></i>
                    <span>{{ $t('marketplace.forgeConsent.approved') }}</span>
                </div>

                <!-- Error from action -->
                <b-alert v-if="actionError" variant="danger" show class="mt-3 mb-0">
                    <i class="bi bi-exclamation-circle-fill mr-2"></i>
                    {{ actionError }}
                </b-alert>

                <hr class="mp-consent-divider" />

                <!-- Footer -->
                <div class="mp-consent-footer">
                    <button
                        v-if="!done"
                        class="mp-consent-btn mp-consent-btn--secondary"
                        @click="deny"
                        :disabled="acting"
                    >
                        {{ $t('cancel') }}
                    </button>

                    <button
                        v-if="!done"
                        class="mp-consent-btn mp-consent-btn--primary"
                        @click="approve"
                        :disabled="acting"
                    >
                        <b-spinner v-if="acting" small class="mr-2" />
                        <i v-else class="bi bi-check-lg mr-2"></i>
                        {{ $t('marketplace.forgeConsent.approve') }}
                    </button>

                    <button
                        v-if="done"
                        class="mp-consent-btn mp-consent-btn--primary mp-consent-btn--full"
                        @click="finish"
                    >
                        <i class="bi bi-arrow-left mr-2"></i>
                        {{ $t('marketplace.forgeConsent.backToApp') }}
                    </button>
                </div>

            </div>

        </div>
    </div>
</template>

<script>
export default {
    name: 'ForgeConsent',

    data() {
        return {
            acting: false,
            done: false,
            actionError: null,
            initError: null,
            grantedToken: null,
            grantedPermissions: null,
        }
    },

    computed: {
        appRef() {
            return this.$route.query.app_ref || ''
        },

        appDisplayName() {
            if (!this.appRef) return ''
            return this.appRef
                .replace(/[-_]/g, ' ')
                .replace(/\b\w/g, function(c) { return c.toUpperCase() })
        },

        permissions() {
            var raw = this.$route.query['permissions[]'] || this.$route.query.permissions
            if (!raw) return []
            if (Array.isArray(raw)) return raw
            return raw.split(',').map(function(p) { return p.trim() }).filter(Boolean)
        },

        reason() {
            return this.$route.query.reason || ''
        },

        returnUrl() {
            return this.$route.query['return'] || ''
        },

        callbackUrl() {
            return this.$route.query.callback || ''
        },
    },

    mounted() {
        if (!this.appRef) {
            this.initError = this.$t('marketplace.forgeConsent.missingAppRef')
            return
        }
        if (!this.permissions || this.permissions.length === 0) {
            this.initError = this.$t('marketplace.forgeConsent.missingPermissions')
        }
    },

    methods: {
        approve() {
            var self = this
            self.acting = true
            self.actionError = null

            self.$http.post('/v1/system/platform/forge/token', {
                app_ref: self.appRef,
                requested_permissions: self.permissions,
            }).then(function(resp) {
                self.grantedToken = resp.data.token
                self.grantedPermissions = resp.data.permissions || []
                self.done = true
                self.acting = false
                self.deliverToken(self.grantedToken, self.grantedPermissions)
            }).catch(function(err) {
                self.acting = false
                var msg = (err.response && err.response.data && err.response.data.message)
                    || self.$t('marketplace.forgeConsent.approveFailed')
                self.actionError = msg
            })
        },

        deny() {
            if (this.returnUrl) {
                window.location.href = this.returnUrl
                return
            }
            if (window.parent !== window) {
                window.parent.postMessage({ type: 'forge_consent_denied' }, '*')
                return
            }
            window.close()
        },

        finish() {
            if (this.returnUrl) {
                window.location.href = this.returnUrl
            } else if (window.parent !== window) {
                // iframe: token already delivered, nothing more to do
                return
            } else {
                window.close()
            }
        },

        deliverToken(token, permissions) {
            if (this.callbackUrl) {
                var sep = this.callbackUrl.indexOf('?') === -1 ? '?' : '&'
                window.location.href = this.callbackUrl + sep + '__forge_token=' + encodeURIComponent(token)
                return
            }
            var msg = {
                type: 'forge_consent_token',
                token: token,
                permissions: permissions,
                app_ref: this.appRef,
            }
            if (window.parent !== window) {
                window.parent.postMessage(msg, '*')
                return
            }
            if (window.opener) {
                window.opener.postMessage(msg, '*')
            }
        },
    },
}
</script>

<style scoped>
.fc-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-page, #0f1117);
    padding: 2rem 1rem;
}

.fc-wrapper {
    width: 100%;
    max-width: 480px;
}

/* Reuse mp-consent-card shell styles */
.mp-consent-card {
    background: var(--bg-card, #1a1d27);
    border: 1px solid var(--border-default, rgba(255,255,255,0.08));
    border-radius: 16px;
    padding: 2rem;
    box-shadow: 0 32px 80px rgba(0, 0, 0, 0.6);
    color: var(--text-primary, #e8eaf0);
}

.mp-consent-product {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
}

.mp-consent-product-icon {
    width: 64px;
    height: 64px;
    border-radius: 14px;
    background: var(--bg-card-secondary, rgba(255,255,255,0.06));
    border: 1px solid var(--border-default, rgba(255,255,255,0.08));
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.75rem;
    color: var(--primary, #5b73e8);
    flex-shrink: 0;
}

.mp-consent-product-info {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
}

.mp-consent-product-name {
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0;
    color: var(--text-primary, #e8eaf0);
    line-height: 1.2;
}

.fc-app-badge {
    font-size: 0.72rem;
    font-weight: 600;
    padding: 2px 10px;
    border-radius: 20px;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    background: rgba(91, 115, 232, 0.15);
    color: #7d97f4;
    border: 1px solid rgba(91, 115, 232, 0.25);
    width: fit-content;
}

.mp-consent-divider {
    border-color: var(--border-default, rgba(255,255,255,0.07));
    margin: 1.5rem 0;
}

/* Request block */
.fc-request-block {
    margin-bottom: 1rem;
}

.fc-request-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--text-primary, #e8eaf0);
    margin-bottom: 0.5rem;
}

.fc-request-reason {
    font-size: 0.825rem;
    color: var(--text-secondary, #8a90a2);
    line-height: 1.6;
    margin: 0;
    padding: 0.625rem 0.875rem;
    background: var(--bg-card-secondary, rgba(255,255,255,0.03));
    border-radius: 8px;
    border-left: 2px solid var(--primary, #5b73e8);
}

/* Permission list */
.fc-perm-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.fc-perm-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.625rem 0.875rem;
    background: var(--bg-card-secondary, rgba(255,255,255,0.03));
    border-radius: 8px;
    border: 1px solid var(--border-default, rgba(255,255,255,0.06));
}

.fc-perm-icon {
    color: #7d97f4;
    font-size: 0.85rem;
    flex-shrink: 0;
}

.fc-perm-name {
    font-size: 0.8rem;
    color: var(--text-primary, #e8eaf0);
    background: rgba(91, 115, 232, 0.1);
    padding: 1px 6px;
    border-radius: 4px;
    font-family: 'SF Mono', 'Fira Code', monospace;
}

/* Success */
.mp-consent-success {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    margin-top: 1.25rem;
    padding: 0.875rem 1rem;
    background: rgba(46, 213, 115, 0.08);
    border: 1px solid rgba(46, 213, 115, 0.2);
    border-radius: 10px;
    color: #2ed573;
    font-size: 0.875rem;
    font-weight: 500;
}

.mp-consent-success i {
    font-size: 1.1rem;
}

/* Error heading/sub */
.mp-consent-error-icon {
    font-size: 2.5rem;
    color: #e55353;
    text-align: center;
    margin-bottom: 1rem;
}

.mp-consent-heading {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--text-primary, #e8eaf0);
    text-align: center;
    margin: 0.5rem 0;
}

.mp-consent-sub {
    font-size: 0.875rem;
    color: var(--text-secondary, #8a90a2);
    text-align: center;
    margin-bottom: 0.5rem;
}

/* Footer */
.mp-consent-footer {
    display: flex;
    gap: 0.75rem;
    margin-top: 0;
    justify-content: flex-end;
}

/* Buttons */
.mp-consent-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: all 0.18s ease;
    min-height: 40px;
    white-space: nowrap;
}

.mp-consent-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.mp-consent-btn--secondary {
    background: transparent;
    border: 1px solid var(--border-hover, rgba(255,255,255,0.15));
    color: var(--text-secondary, #8a90a2);
}

.mp-consent-btn--secondary:hover:not(:disabled) {
    background: var(--bg-card-secondary, rgba(255,255,255,0.05));
    color: var(--text-primary, #e8eaf0);
}

.mp-consent-btn--primary {
    background: var(--primary, #5b73e8);
    color: #fff;
    box-shadow: 0 2px 12px rgba(91, 115, 232, 0.35);
}

.mp-consent-btn--primary:hover:not(:disabled) {
    opacity: 0.88;
    transform: translateY(-1px);
    box-shadow: 0 4px 18px rgba(91, 115, 232, 0.45);
}

.mp-consent-btn--full {
    width: 100%;
}
</style>
